import { lazy, Suspense, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Camera } from "lucide-react";

import { LatestAdsGrid, LatestAdsSkeleton, SellerFilterTabs, useHomeSellerFilter } from "@/components/home/LatestAdsGrid";
import { VideoScanPromo, PhotoScanPromo, DeepScanPromo } from "@/components/VideoScanPromo";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { CATEGORIES } from "@/lib/categories";

import { IMAGE_ACCEPT } from "@/lib/image-input";
import { setPendingAdFiles } from "@/lib/pending-ad-files";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { PLANS, planAtLeast, type PlanId } from "@/lib/plans";
import { cn } from "@/lib/utils";
import { useExpertFeatures } from "@/hooks/useExpertFeatures";
import { useLocale, useTranslation } from "@/lib/i18n";
import { localizedPath } from "@/lib/i18n/slug-map";

const StartCaptureDialog = lazy(() =>
  import("@/components/StartCaptureDialog").then((module) => ({
    default: module.StartCaptureDialog,
  })),
);
const ScanModePicker = lazy(() =>
  import("@/components/scan/ScanModePicker").then((module) => ({ default: module.ScanModePicker })),
);
const AuthModal = lazy(() =>
  import("@/components/auth/AuthModal").then((module) => ({ default: module.AuthModal })),
);

export function HomeSellFlow() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [modeOpen, setModeOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pressed, setPressed] = useState(false);
  const { enabled: testFeatures } = useExpertFeatures();
  const { t } = useTranslation();
  const [seller, setSeller] = useHomeSellerFilter();
  const { lang } = useLocale();
  const local = (to: string) => localizedPath(to, lang);

  const { data: planStatus } = useQuery({
    queryKey: ["my-plan"],
    queryFn: () => getMyPlanStatus(),
    enabled: Boolean(user),
  });

  // Samma bildtak som i skapa-flödet, så kameradialogen kan stoppa i tid.
  const homePlanId = (planStatus?.plan ?? "free") as PlanId;
  const homeMaxImages = PLANS[homePlanId]?.limits.maxImages ?? PLANS.free.limits.maxImages;

  const continueWithFiles = async (files: File[]) => {
    if (!files.length) return;
    setPendingAdFiles(files);
    await navigate({ to: local("/skapa") });
  };

  return (
    <main>
      <section className="relative overflow-x-clip bg-ocean-900 px-4 pb-8 pt-6 text-white sm:px-6 sm:pb-20 sm:pt-16 lg:px-8 lg:pt-20">
        <div className="absolute inset-0 opacity-20" aria-hidden>
          <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-ocean-500 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-ocean-300 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-4xl text-center">
          <h1 className="font-heading text-[1.6rem] font-extrabold leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
            {t("home.heroTitle")}{" "}
            <span className="text-ocean-300">{t("home.heroTitleAccent")}</span>
          </h1>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-ocean-100 sm:mt-6 sm:text-xl">
            {t("home.heroSubtitle")}
          </p>
          <HeroStart pressed={pressed} setPressed={setPressed} onOpen={() => setModeOpen(true)} />
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            multiple
            className="hidden"
            onChange={(event) => {
              const files = Array.from(event.currentTarget.files ?? []);
              event.currentTarget.value = "";
              void continueWithFiles(files);
            }}
          />
        </div>
      </section>

      <Sheet open={modeOpen} onOpenChange={setModeOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="text-left">
            <SheetTitle className="font-heading">{t("home.sheetTitle")}</SheetTitle>
            <SheetDescription>{t("home.sheetDescription")}</SheetDescription>
          </SheetHeader>
          {modeOpen && (
            <Suspense fallback={<div className="mt-4 h-40 animate-pulse rounded-lg bg-muted" />}>
              <ScanModePicker
                className="mt-4"
                isLocked={(mode) =>
                  mode.requiresPlan ? !planAtLeast(planStatus?.plan, mode.requiresPlan) : false
                }
                onSelect={(mode, locked) => {
                  setModeOpen(false);
                  if (mode.id === "single") {
                    // I appen öppnar vi telefonens kamera direkt; annars som vanligt.
                    void (async () => {
                      const { isAppMode, takeNativePhoto, hapticSignal } = await import(
                        "@/app-bridge/core/wrapper"
                      );
                      if (isAppMode()) {
                        const photo = await takeNativePhoto();
                        if (photo) {
                          hapticSignal("light");
                          await continueWithFiles([photo]);
                          return;
                        }
                      }
                      if (isMobile) inputRef.current?.click();
                      else setCaptureOpen(true);
                    })();
                    return;
                  }
                  if (mode.needsAuth && !user) {
                    setAuthOpen(true);
                    return;
                  }
                  if (locked) {
                     void navigate({ to: local("/priser") });
                    return;
                  }
                  const lage =
                    mode.id === "photos" ? "foton" : mode.id === "deep" ? "djup" : "film";
                  void navigate({ to: "/mina-prylar", search: { lage, start: 1 } });
                }}
              />
            </Suspense>
          )}
        </SheetContent>
      </Sheet>

      {captureOpen && (
        <Suspense fallback={null}>
          <StartCaptureDialog
            open
            onOpenChange={setCaptureOpen}
            maxImages={homeMaxImages}
            isFreePlan={homePlanId === "free"}
            onFiles={(files) => void continueWithFiles(files)}
          />
        </Suspense>
      )}
      {authOpen && (
        <Suspense fallback={null}>
          <AuthModal open onOpenChange={setAuthOpen} />
        </Suspense>
      )}

      <section className="px-4 py-6 sm:px-6 sm:py-12 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-4 sm:gap-5 lg:grid-cols-2">
          <VideoScanPromo />
          <PhotoScanPromo />
          {testFeatures && <DeepScanPromo />}
        </div>
      </section>

      <section className="px-4 pb-10 sm:px-6 sm:pb-20 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3 sm:flex sm:flex-wrap sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <h2 className="font-heading text-2xl font-bold text-foreground sm:text-3xl">
                {t("home.latestTitle")}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground sm:mt-2 sm:text-base">
                {t("home.latestSubtitle")}
              </p>

            </div>
            <Button asChild variant="outline">
               <Link to={local("/annonser")} search={seller === "alla" ? undefined : ({ saljare: seller } as never)}>{t("home.seeAll")}</Link>
            </Button>
          </div>
          <SellerFilterTabs value={seller} onChange={setSeller} />
          <Suspense fallback={<LatestAdsSkeleton />}>
            <LatestAdsGrid seller={seller} />
          </Suspense>
          <div className="mt-6 flex flex-wrap justify-center gap-1.5 sm:mt-10 sm:gap-2">
            {CATEGORIES.map((category) => (
              <Link
                key={category.slug}
                 to={local("/annonser")}
                search={{ kategori: category.slug }}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground/80 transition-colors hover:border-primary/50 hover:text-foreground sm:rounded-md sm:px-3 sm:py-1.5 sm:text-sm"
              >
                <category.icon className="h-3.5 w-3.5 text-muted-foreground sm:h-4 sm:w-4" />{" "}
                {category.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function HeroStart({
  pressed,
  setPressed,
  onOpen,
}: {
  pressed: boolean;
  setPressed: (value: boolean) => void;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      className={cn(
        "group relative mx-auto mt-6 block w-full max-w-xl cursor-pointer rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.01] hover:border-ocean-300/30 hover:bg-white/10 hover:shadow-xl hover:shadow-ocean-900/20 sm:mt-10 sm:p-10",
        pressed
          ? "-translate-y-0.5 scale-[1.01] border-ocean-300/30 bg-white/10 shadow-xl"
          : "active:translate-y-0 active:scale-[0.99]",
      )}
    >
      <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-ocean-300 to-ocean-500 shadow-lg shadow-ocean-900/30 transition-transform duration-300 group-hover:scale-110 sm:h-20 sm:w-20">
        <Camera className="h-7 w-7 text-ocean-900 sm:h-9 sm:w-9" />
      </span>
      <span className="mt-4 block font-heading text-lg font-bold text-white sm:text-2xl">
        {t("home.ctaTitle")}
      </span>
      <span className="mx-auto mt-2 block max-w-sm text-sm leading-relaxed text-ocean-100">
        {t("home.ctaSubtitle")}
      </span>
      <span className="relative mt-6 inline-flex items-center gap-2 overflow-hidden rounded-xl bg-ocean-300 px-7 py-3.5 font-heading font-semibold text-ocean-900 shadow-md transition-all duration-300 group-hover:bg-ocean-200">
        {t("home.ctaButton")}{" "}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </button>
  );
}
